import { Lightning, Log, Registry, Router, Utils } from '@lightningjs/sdk';
import { Theme } from '../../config/theme';
import { AtvKeyboardInput } from '../../components/keyboard/AtvKeyboardInput';
import { Button } from '../../components/shared/Button';
import { AtvKeyboardService } from '../../components/keyboard/AtvKeyboardService';
import { ScreenSize } from '../../api/ScreenSize';
import { Login } from '../../Business/Login';
import { TokenApi } from '../../api/bootstrap/TokenApi';
import { StaticPageUrls } from '../../api/analytics/ClientEvents';
import { ErrorTranslationKeys, LoginTranslationKeys } from '../../api/models/TranslationKeys';
import { ShowPasswordButton } from '../../components/ShowPasswordButton';
import { Errors } from '../../api/Errors';

const Constants = {
  Form: {
    margin: {
      top: 384,
      bottom: 44,
    },
    Container: {
      marginTop: 62,
    },
  },
  Label: {
    height: 28,
    MarginTop: 20,
  },
  Input: {
    height: 62,
    width: 584,
    marginTop: 16,
  },
  Error: {
    height: 28,
    MarginTop: 24,
  },
  Button: {
    marginTop: 86,
    height: 60,
  },
  ExtraInfo: {
    marginTop: 20,
  },
};

export class LoginPage extends Lightning.Component {
  static _template() {
    return {
      x: 0,
      y: 0,
      w: w => w,
      h: h => h,
      color: Theme.Colors.Transparent,
      rect: true,
      Background: {
        x: 0,
        y: 0,
        ...ScreenSize,
        src: Utils.asset('images/login/login_background.png'),
      },
      Logo: {
        x: 1920 * 0.5,
        y: 174,
        mountX: 0.5,
        src: Utils.asset('images/login/login_logo.png'),
      },
      LoginForm: {
        y: Constants.Form.margin.top,
        w: w => w,
        FormBackground: {
          w: w => w,
          h: this.formHeight() + Constants.Form.margin.bottom,
          rect: true,
          color: Theme.Colors.Login.Form.background,
        },
        FormContainer: {
          x: 1920 * 0.5,
          y: Constants.Form.Container.marginTop,
          w: Constants.Input.width,
          mountX: 0.5,
          EmailLabel: {
            h: Constants.Label.height,
            text: {
              ...Theme.Fonts.Login.Label,
            },
          },
          EmailInputField: {
            y: Constants.Label.height + Constants.Input.marginTop,
            h: Constants.Input.height,
            w: Constants.Input.width,
            type: AtvKeyboardInput,
            emailMode: true,
            shader: {
              type: Lightning.shaders.RoundedRectangle,
              radius: 6,
            },
            showUnderline: true,
            inputColors: {
              focus: Theme.Colors.Login.Underline.focused,
              unfocus: Theme.Colors.Login.Underline.unfocused,
            },
            textColor: 0xffffffff,
            cursorColor: Theme.Colors.Primary,
            signals: {
              onFinished: '_nextStepLogin',
            },
          },
          EmailError: {
            y: Constants.Label.height + Constants.Input.marginTop + Constants.Input.height + Constants.Error.MarginTop,
            h: Constants.Error.height,
            text: {
              ...Theme.Fonts.Login.Label,
              textColor: Theme.Colors.Login.Label.error,
            },
            alpha: 0,
          },
          PasswordLabel: {
            y: this.fieldHeight() + Constants.Label.MarginTop,
            h: Constants.Label.height,
            text: {
              ...Theme.Fonts.Login.Label,
            },
          },
          PasswordInputField: {
            y: this.fieldHeight() + Constants.Label.height + Constants.Input.marginTop + Constants.Label.MarginTop,
            h: Constants.Input.height,
            w: Constants.Input.width,
            type: AtvKeyboardInput,
            passwordMode: true,
            showUnderline: true,
            textRightMargin: 85,
            inputColors: {
              focus: Theme.Colors.Login.Underline.focused,
              unfocus: Theme.Colors.Login.Underline.unfocused,
            },
            textColor: 0xffffffff,
            cursorColor: Theme.Colors.Primary,
            shader: {
              type: Lightning.shaders.RoundedRectangle,
              radius: 6,
            },
            signals: {
              onFinished: '_nextStepLogin',
            },
          },
          ShowPasswordIcon: {
            x: 504,
            y: this.fieldHeight() + Constants.Label.height + Constants.Input.marginTop + Constants.Label.MarginTop - 20,
            type: ShowPasswordButton,
            signals: {
              onActivate: '_handlePasswordButton',
            },
          },
          PasswordError: {
            y:
              this.fieldHeight() +
              Constants.Label.MarginTop +
              Constants.Label.height +
              Constants.Input.marginTop +
              Constants.Input.height +
              Constants.Error.MarginTop,
            h: Constants.Error.height,
            text: {
              ...Theme.Fonts.Login.Label,
              textColor: Theme.Colors.Login.Label.error,
            },
            alpha: 0,
          },
        },
      },
      FormButton: {
        x: 1920 * 0.5,
        mountX: 0.5,
        y: Constants.Form.margin.top + this.formHeight() + Constants.Form.margin.bottom + Constants.Button.marginTop,
        type: Button,
        buttonMode: 'primary',
        w: 164,
        signals: {
          onActivate: '_handleLoginButton',
        },
      },
      ExtraInfoLabel: {
        x: 1920 * 0.5,
        mountX: 0.5,
        y:
          Constants.Form.margin.top +
          this.formHeight() +
          Constants.Form.margin.bottom +
          Constants.Button.marginTop +
          Constants.Button.height +
          Constants.ExtraInfo.marginTop,
        text: {
          ...Theme.Fonts.Login.Label,
          textAlign: 'center',
          text: '',
          wordWrap: true,
          wordWrapWidth: 1920 * (2 / 3),
          maxLines: 2,
        },
      },
    };
  }

  _onChanged() {
    Log.info('[LoginPage] _init params:', this.params);
    AtvKeyboardService.init(this);
    this.focusList = [
      this.tag('LoginForm').tag('EmailInputField'),
      this.tag('LoginForm').tag('PasswordInputField'),
      this.tag('FormButton'),
    ];
    this._changeFocusIndex(0);
    const backend = this.params.backend;
    this.tag('EmailLabel').text.text = backend.translations.forKey(LoginTranslationKeys.login_name);
    this.tag('PasswordLabel').text.text = backend.translations.forKey(LoginTranslationKeys.login_password);
    this.tag('FormButton').label = backend.translations.forKey(LoginTranslationKeys.login_login);
    this.tag('EmailError').text.text = backend.translations.forKey(ErrorTranslationKeys.error_user);
    this.tag('PasswordError').text.text = backend.translations.forKey(ErrorTranslationKeys.error_user);
    const extraInfo = backend.translations.forKey(LoginTranslationKeys.login_extra_information);
    if (extraInfo && extraInfo.length > 0 && extraInfo !== `*${LoginTranslationKeys.login_extra_information}*`) {
      this.tag('ExtraInfoLabel').text.text = extraInfo;
    }
    this._focusShowPassword = false;
    this._showUsernameError = false;
    this._showPasswordError = false;
    this.params.backend.logService.trackPageView(StaticPageUrls.login);
  }

  _getFocused() {
    if (AtvKeyboardService.instance?.isOpen) {
      return AtvKeyboardService.instance.keyboard;
    }
    if (this._focusShowPassword) {
      return this.tag('ShowPasswordIcon');
    }
    return this.focusList[this.focusIndex];
  }

  _handleDown() {
    this._changeFocusIndex(Math.min(this.focusIndex + 1, this.focusList.length - 1));
    this._focusShowPassword = false;
  }

  _handleUp() {
    this._changeFocusIndex(Math.max(this.focusIndex - 1, 0));
    this._focusShowPassword = false;
  }

  _handleRight() {
    if (this.focusList[this.focusIndex] === this.tag('LoginForm').tag('PasswordInputField')) {
      this._focusShowPassword = true;
    }
  }

  _handleLeft() {
    if (this._focusShowPassword) {
      this._focusShowPassword = false;
    }
  }

  _changeFocusIndex(newIndex) {
    this._updateErrors();
    this.focusIndex = newIndex;
    if (this.focusIndex === 0) {
      this.tag('LoginForm').tag('EmailLabel').text.textColor = this._getLabelColor(this._showUsernameError, true);
      this.tag('LoginForm').tag('PasswordLabel').text.textColor = this._getLabelColor(this._showPasswordError, false);
    } else if (this.focusIndex === 1) {
      this.tag('LoginForm').tag('EmailLabel').text.textColor = this._getLabelColor(this._showUsernameError, false);
      this.tag('LoginForm').tag('PasswordLabel').text.textColor = this._getLabelColor(this._showPasswordError, true);
    } else {
      this.tag('LoginForm').tag('EmailLabel').text.textColor = this._getLabelColor(this._showUsernameError, false);
      this.tag('LoginForm').tag('PasswordLabel').text.textColor = this._getLabelColor(this._showPasswordError, false);
    }
  }

  _getLabelColor(showError, focused) {
    if (focused) {
      if (showError) {
        return Theme.Colors.Login.Label.error;
      }
      return Theme.Colors.Login.Label.focused;
    } else {
      return Theme.Colors.Login.Label.unfocused;
    }
  }

  _nextStepLogin() {
    this._handleDown();
    this._refocus();
    if (this.focusList[this.focusIndex] === this.tag('LoginForm').tag('PasswordInputField')) {
      // after username, open keyboard on password field. Needs timeout so that Cursor can disappear on username field
      Registry.setTimeout(() => {
        this.tag('LoginForm').tag('PasswordInputField')._handleEnter();
      });
    }
    if (this.focusList[this.focusIndex] === this.tag('FormButton')) {
      this._handleLoginButton();
    }
  }

  _handleLoginButton() {
    const params = { backend: this.params.backend, reset: true };
    if (this.tag('EmailInputField').input && this.tag('PasswordInputField').input) {
      TokenApi.authorize(this.params.backend, this.tag('EmailInputField').input, this.tag('PasswordInputField').input)
        .then(token => {
          this.params.backend.token = token;
          TokenApi.save(token);
          return Login.initSession(this.params.backend);
        })
        .then(() => {
          this.params.backend.playerController.fallbackToLinearStream();
          this.clearFields();
          Router.navigate('main', params);
        })
        .catch(err => {
          let message = this.params.backend.translations.forKey(ErrorTranslationKeys.error_general);
          if (err.data?.error_hint) {
            const translation = this.params.backend.translations.forKey(err.data.error_hint);
            if (translation !== `*${err.data.error_hint}*`) {
              // if translation exist, use it
              message = translation;
            } else {
              message = this.params.backend.translations.forKey(ErrorTranslationKeys.error_user);
            }
          } else if (err.message === Errors.InvalidGrant) {
            message = this.params.backend.translations.forKey(ErrorTranslationKeys.error_user) || message;
          }
          this.fireAncestors('$showMessage', message);
        });
    } else {
      this._showUsernameError = !this.tag('EmailInputField').input;
      this._showPasswordError = !this.tag('PasswordInputField').input;
      this._updateErrors();
    }
  }

  _updateErrors() {
    if (this._showUsernameError && !this.tag('EmailInputField').input) {
      this.tag('EmailInputField').inputColors = {
        focus: Theme.Colors.Login.Underline.error,
        unfocus: Theme.Colors.Login.Underline.error,
      };
      this.tag('EmailError').setSmooth('alpha', 1);
    } else {
      this._showUsernameError = false;
      this.tag('EmailInputField').inputColors = {
        focus: Theme.Colors.Login.Underline.focused,
        unfocus: Theme.Colors.Login.Underline.unfocused,
      };
      this.tag('EmailError').setSmooth('alpha', 0);
    }
    if (this._showPasswordError && !this.tag('PasswordInputField').input) {
      this.tag('PasswordInputField').inputColors = {
        focus: Theme.Colors.Login.Underline.error,
        unfocus: Theme.Colors.Login.Underline.error,
      };
      this.tag('PasswordError').setSmooth('alpha', 1);
    } else {
      this._showPasswordError = false;
      this.tag('PasswordInputField').inputColors = {
        focus: Theme.Colors.Login.Underline.focused,
        unfocus: Theme.Colors.Login.Underline.unfocused,
      };
      this.tag('PasswordError').setSmooth('alpha', 0);
    }
  }

  _handlePasswordButton(val) {
    this.tag('LoginForm').tag('PasswordInputField').passwordMode = val;
  }

  _inactive() {
    AtvKeyboardService.removeInstance();
    this.clearFields();
  }

  static formHeight() {
    return Constants.Form.Container.marginTop + this.fieldHeight() * 2 + Constants.Label.MarginTop;
  }

  clearFields() {
    this.tag('EmailInputField').clear();
    this.tag('PasswordInputField').clear();
  }

  static fieldHeight() {
    return (
      Constants.Label.height +
      Constants.Input.marginTop +
      Constants.Input.height +
      Constants.Error.MarginTop +
      Constants.Error.height
    );
  }
}
